// Documentos impresos de la OD (docs/110 §15, §22; formato Random pedido por Rodrigo, 2026-10-13):
// manifiesto de carga por súper familia y hoja de rendición por documento.
import { decimal } from '../comun/formato';
import { EncabezadoImpreso } from '../comun/Imprimir';
import type { DocumentosNotaOd, Od, ProductoManifiesto } from './datos';
import { textoManifiesto, textoRendicion, type LineaReporte } from './reporteRandom';

/** Un reporte de texto con el formato de Random, en monoespaciado. */
export function Reporte({ lineas, horizontal }: { lineas: LineaReporte[]; horizontal?: boolean }) {
  return (
    <article className={`impreso impreso-random${horizontal ? ' impreso-horizontal' : ''}`}>
      <pre>
        {lineas.map((l, i) =>
          l.titulo ? (
            <b key={i} className="titulo-random">
              {l.texto}
              {'\n'}
            </b>
          ) : l.partes ? (
            <span key={i} className="detalle-random renglon-random">
              <span className="producto-random">{l.partes.producto}</span>
              <b className="cantidad-random">{l.partes.cantidad}</b>
            </span>
          ) : (
            <span key={i} className={l.detalle ? 'detalle-random' : undefined}>
              {l.texto}
              {'\n'}
            </span>
          ),
        )}
      </pre>
    </article>
  );
}

interface EmpresaImpreso {
  razonSocial: string;
  rut: string;
  dv: string;
}

/** Manifiesto de carga, igual al de Random (pedido de Rodrigo, 2026-10-13). */
export function ManifiestoImpreso({
  od,
  productos,
  resumen,
  empresa,
  bodega,
}: {
  od: Od;
  productos: ProductoManifiesto[];
  resumen?: { kilos: string; notas: number; clientes: number } | undefined;
  empresa: EmpresaImpreso;
  bodega: string;
}) {
  return <Reporte lineas={textoManifiesto(od, productos, resumen, empresa, bodega)} />;
}

/** Hoja de rendición, igual a la de Random. */
export function HojaRendicionImpresa({
  od,
  documentos,
  empresa,
  bodega,
}: {
  od: Od;
  documentos: DocumentosNotaOd[];
  empresa: EmpresaImpreso;
  bodega: string;
}) {
  return <Reporte horizontal lineas={textoRendicion(od, documentos, empresa, bodega)} />;
}

/** Un producto sumado sobre varias OD (carga consolidada, como «Consolidar varias ODD» en Random). */
export interface ProductoConsolidado {
  productoId: string;
  sku: string;
  descripcion: string;
  total: string;
  porOd: Record<number, string>;
}

/** Suma los manifiestos de varias OD por producto (las cantidades llegan como texto decimal). */
export function consolidarManifiestos(
  manifiestos: Array<{ numero: number; productos: ProductoManifiesto[] }>,
): ProductoConsolidado[] {
  const mapa = new Map<string, ProductoConsolidado>();
  for (const m of manifiestos)
    for (const p of m.productos) {
      const x = mapa.get(p.productoId) ?? {
        productoId: p.productoId,
        sku: p.sku,
        descripcion: p.descripcion,
        total: '0',
        porOd: {},
      };
      x.total = String(Number(x.total) + Number(p.total));
      x.porOd[m.numero] = String(Number(x.porOd[m.numero] ?? 0) + Number(p.total));
      mapa.set(p.productoId, x);
    }
  return [...mapa.values()].sort((a, b) => a.descripcion.localeCompare(b.descripcion));
}

export function CargaConsolidadaImpresa({
  empresa,
  ordenes,
  productos,
}: {
  empresa: string;
  ordenes: Array<{ numero: number; patente: string | null; ruta: string | null; notas: number }>;
  productos: ProductoConsolidado[];
}) {
  const total = productos.reduce((n, p) => n + Number(p.total), 0);
  return (
    <article className="impreso">
      <EncabezadoImpreso
        empresa={empresa}
        titulo="Carga consolidada"
        numero={`ODD N° ${ordenes.map((o) => o.numero).join(', ')}`}
        detalle={
          <dl className="impreso-datos">
            {ordenes.map((o) => (
              <div key={o.numero}>
                <dt>ODD {o.numero}</dt>
                <dd>
                  {o.patente ?? 'sin camión'} · {o.ruta ?? 'sin ruta'} · {o.notas} notas
                </dd>
              </div>
            ))}
          </dl>
        }
      />
      <table className="impreso-tabla">
        <thead>
          <tr>
            <th>SKU</th>
            <th>Producto</th>
            {ordenes.map((o) => (
              <th key={o.numero} className="der">
                ODD {o.numero}
              </th>
            ))}
            <th className="der">Total</th>
            <th className="der">Preparado ✓</th>
          </tr>
        </thead>
        <tbody>
          {productos.map((p) => (
            <tr key={p.productoId}>
              <td className="mono">{p.sku}</td>
              <td>{p.descripcion}</td>
              {ordenes.map((o) => (
                <td key={o.numero} className="der">
                  {p.porOd[o.numero] ? decimal(p.porOd[o.numero]!) : ''}
                </td>
              ))}
              <td className="der">
                <b>{decimal(p.total)}</b>
              </td>
              <td className="casilla" />
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2 + ordenes.length}>{productos.length} productos</td>
            <td className="der">
              <b>{decimal(String(total))}</b>
            </td>
            <td />
          </tr>
        </tfoot>
      </table>
      <footer className="impreso-firmas">
        <span>Preparó</span>
        <span>Validó carga</span>
      </footer>
    </article>
  );
}

/** CSV para Excel (punto y coma, BOM y coma decimal): una fila por producto, columnas por OD. */
export function csvCargaConsolidada(ordenes: number[], productos: ProductoConsolidado[]): string {
  const esc = (x: string) => (/[;"\n]/.test(x) ? `"${x.replace(/"/g, '""')}"` : x);
  const num = (x: string | undefined) => (x ? String(Number(x)).replace('.', ',') : '');
  const filas = [
    ['SKU', 'Producto', ...ordenes.map((n) => `ODD ${n}`), 'Total'].join(';'),
    ...productos.map((p) =>
      [esc(p.sku), esc(p.descripcion), ...ordenes.map((n) => num(p.porOd[n])), num(p.total)].join(
        ';',
      ),
    ),
  ];
  return `${String.fromCharCode(0xfeff)}${filas.join('\r\n')}`;
}
